import React from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2 } from "lucide-react";

function CategoryPill({ category }) {
  return (
    <span
      className="inline-flex items-center h-[18px] rounded-[9px] whitespace-nowrap leading-none text-xs"
      style={{ background: 'rgba(127,199,249,0.20)', color: '#377AD6', padding: '2px 8px 2px 4px', fontWeight: 500 }}
    >
      {category || '—'}
    </span>
  );
}

function StatusPill({ status }) {
  const config = {
    'Active':       { bg: 'rgba(209,238,176,0.50)', color: '#4DB300' },
    'Under Review': { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(233,171,48,0.90)' },
    'Deprecated':   { bg: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)' },
  };
  const cfg = config[status] || { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)' };
  return (
    <span
      className="inline-flex items-center gap-1 h-[18px] rounded-[9px] whitespace-nowrap leading-none text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color, padding: '2px 8px 2px 3px' }}
    >
      <span className="size-[10px] rounded-full shrink-0" style={{ background: cfg.color }} />
      {status || '—'}
    </span>
  );
}

const COL = {
  name:    'flex-1 min-w-0',
  cat:     'w-[140px] shrink-0',
  status:  'w-[130px] shrink-0',
  values:  'w-[80px]  shrink-0',
  usedBy:  'w-[110px] shrink-0',
  actions: 'w-[60px]  shrink-0',
};

export default function ReferenceListsTable({ lists, isLoading, onDeleteList, onRowClick, valueCounts = {} }) {
  if (isLoading) {
    return (
      <div className="flex flex-col min-w-[900px]">
        <div className="h-9 rounded-t-[8px]" style={{ background: 'var(--table-header-bg)' }} />
        <div className="flex flex-col">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-11 px-5 flex items-center" style={{ background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--table-stripe)' }}>
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (lists.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-sm text-page-muted">No reference lists found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-w-[900px]">
      {/* Header */}
      <div
        className="flex items-center h-9 px-5 gap-[10px] border border-table-header"
        style={{ background: 'var(--table-header-bg)', borderRadius: '8px 8px 0 0' }}
      >
        <div className={`${COL.name} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>List name</div>
        <div className={`${COL.cat} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Category</div>
        <div className={`${COL.status} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Status</div>
        <div className={`${COL.values} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Values</div>
        <div className={`${COL.usedBy} text-sm`} style={{ color: 'var(--table-header-color)', fontWeight: 500 }}>Used by</div>
        <div className={COL.actions} />
      </div>

      {/* Rows */}
      {lists.map((list, i) => {
        const usageCount = parseInt(list.usage_count, 10) || 0;
        const rowBg = i % 2 === 0 ? 'var(--table-stripe)' : 'var(--card-bg)';
        return (
          <div
            key={list.id}
            className="group flex items-center h-11 px-5 gap-[10px] cursor-pointer"
            style={{ background: rowBg }}
            onClick={() => onRowClick?.(list)}
          >
            <div className={`${COL.name} min-w-0`}>
              <div className="text-sm font-medium truncate text-page-text group-hover:text-nav-active transition-colors">{list.list_name}</div>
              {list.description && (
                <div className="text-xs font-normal truncate" style={{ color: 'var(--page-muted)' }}>{list.description}</div>
              )}
            </div>
            <div className={COL.cat}>
              {list.category ? <CategoryPill category={list.category} /> : <span style={{ color: 'var(--page-muted)' }}>—</span>}
            </div>
            <div className={COL.status}>
              <StatusPill status={list.status} />
            </div>
            <div className={COL.values}>
              <span className="text-sm font-normal" style={{ color: 'var(--page-text)' }}>{valueCounts[list.id] ?? 0}</span>
            </div>
            <div className={COL.usedBy}>
              <span className="text-sm font-normal" style={{ color: 'var(--page-text)' }}>
                {usageCount} element{usageCount !== 1 ? 's' : ''}
              </span>
            </div>
            <div className={`${COL.actions} flex justify-end`} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => onDeleteList(list)}
                title="Delete list"
                disabled={usageCount > 0}
                className="flex items-center justify-center bg-transparent border-0 cursor-pointer text-nav-hover hover:text-nav-active transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
